import {
  createContext,
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { toast } from '@/lib/v2/components';

import { velocityBar } from '@/src/ContactsModule/components/ContactsViewInfo/ProgressVelocityEnum';
import { useService } from '@/src/infrastructure/Protocol/useEmblue';
import { importStatus, loadingStatus } from '@/src/presentation/types/enum/importStatus.enum';
import { IAnalysisResult } from '@/src/presentation/types/interfaces/IAnalysisResult.interface';
import { FileStateProps } from '@/src/presentation/types/interfaces/IImport.interface';

import { getDataCard } from './DataCard';

import { ImportSteps } from '@/modules/ContactsModule/screens/ImportMain/ImportStepEnum.enum';

interface IMutationFileContext {
  setFiles: Function;
  setFileName: Dispatch<SetStateAction<string>>;
}

interface IFileStateContext {
  files: FileStateProps | undefined;
  fileName: string;
}

interface IVerifyState {
  verifyImportState: Function;
}

interface ContactsProviderProps {
  fileStatesProps?: Partial<FileStateProps>;
  children: ReactNode;
}

interface ILoading {
  loadingBar: Function;
}

export const StateFileContext = createContext<IFileStateContext | undefined>(undefined);
export const MutationFileContext = createContext<IMutationFileContext | undefined>(undefined);
export const MutationVerifyState = createContext<IVerifyState | undefined>(undefined);
export const MutationLoadingBar = createContext<ILoading | undefined>(undefined);

const FileProvider: FC<ContactsProviderProps> = ({ fileStatesProps, children }): JSX.Element => {
  const [flag, setFlag] = useState<boolean>(false);
  const { t } = useTranslation();
  const [fileName, setFileName] = useState<string>('');

  const [files, setFiles] = useState<FileStateProps>({
    step: ImportSteps.import,
    showStateCard: false,
    state: loadingStatus.loading,
    importLoading: {
      step: 0,
      mode: velocityBar.slow,
      percentage: 0,
      dataCard: getDataCard(),
    },
    ...fileStatesProps,
  });
  const service = useService();
  const timer = useRef<NodeJS.Timer>();

  const memoizedMutation = useMemo<IMutationFileContext>(
    () => ({
      setFiles,
      setFileName,
    }),
    [setFiles, setFileName]
  );

  const checkImportStatus = useCallback(
    async (
      process: number,
      file: FileStateProps
    ): Promise<{ analysis: IAnalysisResult | undefined; status: string }> => {
      try {
        const response = await service?.importStatus({ idProcess: process });
        if (
          response === importStatus.preImportDataAvailable ||
          response === importStatus.endConcatenation
        ) {
          const analysis: IAnalysisResult | undefined = await service?.analysisResult({
            idProcess: process,
          });
          if (analysis) {
            if (file.groups) {
              return { analysis, status: response };
            }
          }
        } else if (response === importStatus.preImportError) {
          toast({
            title: t('IMPORT_MAIN.errorLoadingTitle'),
            variant: 'error',
            body: t('IMPORT_MAIN.errorLoadingBody'),
          });
          return { analysis: undefined, status: response };
        }
      } catch (e) {
        console.error(e);
      }
      return { analysis: undefined, status: '' };
    },
    [service]
  );

  const verifyImportState = useCallback(
    (process: number) => {
      const interval = setInterval(async () => {
        const done = await checkImportStatus(process, files);
        if (
          (done && done.status === importStatus.preImportDataAvailable) ||
          done.status === importStatus.endConcatenation ||
          done.status === importStatus.preImportError
        ) {
          clearInterval(interval);
          done.status === importStatus.preImportError && setFlag(false);
          setFiles((prevFiles) => {
            return {
              ...prevFiles,
              analysisResult: done.analysis,
              process,
              groups: files?.groups,
              state: done.analysis ? loadingStatus.loading : loadingStatus.error,
              step: ImportSteps.info,
            };
          });
        }
      }, 3000);
    },
    [checkImportStatus, files, setFiles]
  );

  const memoizedVerifyState = useMemo<IVerifyState>(
    () => ({
      verifyImportState,
    }),
    [verifyImportState]
  );

  const loadingBar = useCallback(() => {
    setFlag(true);
  }, []);

  const memoizedLoadingBar = useMemo<ILoading>(
    () => ({
      loadingBar,
    }),
    [loadingBar]
  );

  useEffect(() => {
    let step = files.importLoading?.step || 0;
    let percentage = files.importLoading?.percentage || 0;
    const length = (files.importLoading?.dataCard?.length || 4) - 1;
    const analysisResult = files?.analysisResult || undefined;

    if (flag) {
      timer.current = setInterval(() => {
        if (!(!analysisResult && percentage + 100 / length >= 100)) {
          step = step + 1;
          percentage = percentage + 100 / length;
        }

        setFiles((prevFiles) => {
          const state =
            percentage === 100
              ? loadingStatus.ready
              : files?.state !== loadingStatus.error
                ? loadingStatus.loading
                : loadingStatus.error;
          return {
            ...prevFiles,
            state: state,
            importLoading: {
              ...prevFiles.importLoading,
              percentage: percentage,
              step: step,
              mode: analysisResult ? velocityBar.medium : velocityBar.slow,
            },
          };
        });
      }, files.importLoading?.mode);
    }

    if (step === length && timer.current) {
      clearInterval(timer.current);
      setFlag(false);
    }
    return () => clearInterval(timer.current);
  }, [files, flag, setFlag]);

  return (
    <MutationFileContext.Provider value={memoizedMutation}>
      <StateFileContext.Provider value={{ files, fileName }}>
        <MutationVerifyState.Provider value={memoizedVerifyState}>
          <MutationLoadingBar.Provider value={memoizedLoadingBar}>
            {children}
          </MutationLoadingBar.Provider>
        </MutationVerifyState.Provider>
      </StateFileContext.Provider>
    </MutationFileContext.Provider>
  );
};

export const useStateFileContext = (): IFileStateContext =>
  useGetContext<IFileStateContext>(StateFileContext);
export const useMutationFileContext = (): IMutationFileContext =>
  useGetContext<IMutationFileContext>(MutationFileContext);
export const useMutationVerifyState = (): IVerifyState =>
  useGetContext<IVerifyState>(MutationVerifyState);
export const useMutationLoadingBar = (): ILoading => useGetContext<ILoading>(MutationLoadingBar);

const useGetContext = <T,>(contextName: React.Context<T | undefined>): T => {
  const context = useContext<T | undefined>(contextName);
  if (!context) {
    throw new Error('FileProvider is required');
  }
  return context;
};

export default FileProvider;
